// =============================================================================
// SHARE STORIES MODAL - Instagram Stories Share Modal with Template Selector
// =============================================================================

import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import {
  generateStoriesCard,
  downloadStoriesCard,
  shareStoriesNative,
  copyStoriesCard,
  generateStoriesShareText,
  type StoriesCardConfig,
  type StoriesTemplate,
  type GameStateData,
  type DecisionData,
} from '../../services/storiesCardGenerator';
import { trackShare } from '../../services/analytics';

interface ShareStoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameState: GameStateData;
  decision?: DecisionData;
}

export function ShareStoriesModal({
  isOpen,
  onClose,
  gameState,
  decision,
}: ShareStoriesModalProps) {
  const [selectedTemplate, setSelectedTemplate] =
    useState<StoriesTemplate>('decision');
  const [cardImage, setCardImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [shareStatus, setShareStatus] = useState<string | null>(null);

  // Generate card when template changes
  useEffect(() => {
    if (!isOpen) return;

    const generateCard = async () => {
      setIsGenerating(true);
      try {
        const config: StoriesCardConfig = {
          template: selectedTemplate,
          gameState,
          decision,
        };
        const dataUrl = await generateStoriesCard(config);
        setCardImage(dataUrl);
      } catch (error) {
        console.error('Failed to generate stories card:', error);
      } finally {
        setIsGenerating(false);
      }
    };

    generateCard();
  }, [isOpen, selectedTemplate, gameState, decision]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShareStatus(null);
      // Auto-select best template based on context
      if (decision?.criticality === 'critical') {
        setSelectedTemplate('critical');
      } else if (decision) {
        setSelectedTemplate('decision');
      } else {
        setSelectedTemplate('dashboard');
      }
    }
  }, [isOpen, decision]);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const getConfig = useCallback(
    (): StoriesCardConfig => ({
      template: selectedTemplate,
      gameState,
      decision,
    }),
    [selectedTemplate, gameState, decision],
  );

  const handleShare = useCallback(async () => {
    const config = getConfig();
    const shareText = generateStoriesShareText(config);
    trackShare('native', 'stalemate');

    const success = await shareStoriesNative(config, shareText);
    if (success) {
      setShareStatus('Успішно поділено!');
      setTimeout(() => {
        setShareStatus(null);
        onClose();
      }, 1500);
    } else {
      // Fallback to download
      await downloadStoriesCard(config);
      setShareStatus('Завантажено — додайте до Stories');
      setTimeout(() => setShareStatus(null), 3000);
    }
  }, [getConfig, onClose]);

  const handleDownload = useCallback(async () => {
    const config = getConfig();
    trackShare('clipboard', 'stalemate');
    await downloadStoriesCard(config);
    setShareStatus('Завантажено!');
    setTimeout(() => setShareStatus(null), 2000);
  }, [getConfig]);

  const handleCopy = useCallback(async () => {
    const config = getConfig();
    trackShare('clipboard', 'stalemate');
    const success = await copyStoriesCard(config);
    if (success) {
      setShareStatus('Скопійовано в буфер!');
    } else {
      await downloadStoriesCard(config);
      setShareStatus('Завантажено (копіювання недоступне)');
    }
    setTimeout(() => setShareStatus(null), 2500);
  }, [getConfig]);

  const templates: Array<{
    id: StoriesTemplate;
    name: string;
    icon: string;
    description: string;
  }> = [
    {
      id: 'decision',
      name: 'РІШЕННЯ',
      icon: '⚡',
      description: 'Покажіть ваш вибір',
    },
    {
      id: 'dashboard',
      name: 'СТАН',
      icon: '📊',
      description: 'Огляд показників',
    },
    {
      id: 'critical',
      name: 'МОМЕНТ',
      icon: '🔥',
      description: 'Драматичний акцент',
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-lg bg-war-panel border border-war-dim/30 shadow-2xl max-h-[95vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-war-dim/20">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📤</span>
                <div>
                  <h2 className="text-war-bright font-mono font-bold text-lg">
                    INSTAGRAM STORIES
                  </h2>
                  <p className="text-war-dim text-xs font-mono">
                    Формат 9:16 для Stories
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-war-dim hover:text-war-bright transition-colors p-2"
                aria-label="Закрити"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Template Selector */}
            <div className="px-5 py-4 border-b border-war-dim/20">
              <p className="text-war-dim text-xs font-mono mb-3 uppercase tracking-wider">
                Оберіть шаблон:
              </p>
              <div className="grid grid-cols-3 gap-2">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`
                                            p-3 border-2 transition-all font-mono text-center
                                            ${
                                              selectedTemplate === template.id
                                                ? 'border-war-primary bg-war-primary/10 text-war-primary'
                                                : 'border-war-dim/30 text-war-dim hover:border-war-dim/50 hover:text-war-bright'
                                            }
                                        `}
                  >
                    <span className="text-xl block mb-1">{template.icon}</span>
                    <span className="text-xs font-bold block">
                      {template.name}
                    </span>
                    <span className="text-[10px] opacity-60 block mt-1">
                      {template.description}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="flex-1 overflow-auto p-5">
              <div className="relative aspect-[9/16] bg-war-bg/50 border border-war-dim/20 overflow-hidden max-h-[400px] mx-auto">
                {isGenerating ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-3 text-war-dim">
                      <div className="w-5 h-5 border-2 border-war-primary border-t-transparent rounded-full animate-spin" />
                      <span className="font-mono text-sm">Генерація...</span>
                    </div>
                  </div>
                ) : cardImage ? (
                  <img
                    src={cardImage}
                    alt="Stories Preview"
                    className="w-full h-full object-contain"
                  />
                ) : null}
              </div>
            </div>

            {/* Status Message */}
            {shareStatus && (
              <div className="px-5 py-2 bg-war-primary/10 border-t border-war-primary/30">
                <p className="text-war-primary text-sm font-mono text-center">
                  ✓ {shareStatus}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="px-5 py-4 border-t border-war-dim/20 space-y-3">
              {/* Primary Share Button */}
              <button
                onClick={handleShare}
                disabled={isGenerating}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z" />
                  <circle cx="12" cy="12" r="3.5" />
                </svg>
                ПОДІЛИТИСЯ В INSTAGRAM
              </button>

              {/* Secondary Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleDownload}
                  disabled={isGenerating}
                  className="py-3 border-2 border-war-dim/40 text-war-bright hover:border-war-primary hover:text-war-primary disabled:opacity-50 disabled:cursor-not-allowed font-mono text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  ЗАВАНТАЖИТИ
                </button>
                <button
                  onClick={handleCopy}
                  disabled={isGenerating}
                  className="py-3 border-2 border-war-dim/40 text-war-bright hover:border-war-primary hover:text-war-primary disabled:opacity-50 disabled:cursor-not-allowed font-mono text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                    />
                  </svg>
                  КОПІЮВАТИ
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
